import { PropsWithChildren, useEffect } from 'react';
import { useRouter } from '@tanstack/react-router';
import { AuthProvider } from '../../contexts/auth-context';
import { getPublicEnvVar, isProduction } from '@upcaret/env-utils';
import type { PostHogConfig } from 'posthog-js';

export function AppProviders({ children }: PropsWithChildren) {
  const router = useRouter();

  // Evaluated at render time so env vars are available (avoids SSR module runner crash
  // that occurs when import.meta.env / globalThis.__UPCARET_PUBLIC_ENV__ is accessed
  // at module evaluation time before the runner context is fully initialized).
  const posthogKey =
    getPublicEnvVar('VITE_UPCARET_PUBLIC_POSTHOG_KEY') ||
    getPublicEnvVar('VITE_PUBLIC_POSTHOG_KEY');
  const posthogHost = isProduction() ? '/ph' : 'https://us.i.posthog.com';
  const posthogEnabled = Boolean(posthogKey) && isProduction();

  useEffect(() => {
    if (!posthogEnabled || typeof window === 'undefined') return;

    let unsubscribeRoute: (() => void) | null = null;
    let posthog: typeof import('posthog-js') | null = null;

    const posthogOptions: Partial<PostHogConfig> = {
      api_host: posthogHost,
      person_profiles: 'identified_only',
      capture_pageview: false,
      capture_pageleave: true,
      autocapture: isProduction(),
      session_recording: {
        recordCrossOriginIframes: false,
        maskAllInputs: false,
        maskInputOptions: {
          password: true,
        },
      },
      loaded: (client) => {
        client.capture('$pageview');
        unsubscribeRoute = router.subscribe('onResolved', (event) => {
          if (event.hrefChanged || event.pathChanged || event.hashChanged) {
            client.capture('$pageview');
          }
        });
      },
    };

    import('posthog-js')
      .then((mod) => {
        posthog = mod;
        posthog.default.init(posthogKey!, posthogOptions);
      })
      .catch((err) => {
        console.error('PostHog init failed', err);
      });

    return () => {
      unsubscribeRoute?.();
      posthog?.default?.shutdown?.();
    };
  }, [router, posthogEnabled, posthogKey, posthogHost]);

  return <AuthProvider>{children}</AuthProvider>;
}
